import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Bloque, MANUAL, MANUAL_ACTUALIZADO, Pregunta, Seccion } from './manual-content';
import './user-manual.style.css';

/**
 * Manual de usuario (preguntas frecuentes) como página de la app: índice con
 * enlaces, preguntas desplegables y buscador. El texto es el del PDF, que sigue
 * disponible para descargar.
 *
 * Enlaces: cambiapp.net/faq#2-3 abre y muestra la pregunta 2.3; #s5 va a la
 * sección 5. Cada pregunta tiene un botón para copiar su enlace.
 */

/** Sin acentos ni mayúsculas, para que "publicacion" encuentre "publicación". */
const normalizar = (s: string) =>
	s
		.toLowerCase()
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '');

const textoDeBloques = (bloques: Bloque[]): string =>
	bloques
		.map((b) => (b.tipo === 'p' ? b.texto ?? '' : (b.items ?? []).map((i) => `${i.texto} ${(i.sub ?? []).join(' ')}`).join(' ')))
		.join(' ');

/** Resalta las palabras buscadas dentro de un texto. */
const Resaltado = ({ texto, palabras }: { texto: string; palabras: string[] }) => {
	if (palabras.length === 0) return <>{texto}</>;
	const base = normalizar(texto);
	// Marcas [inicio, fin) sobre el texto original (normalizar no cambia el largo).
	const marcas: [number, number][] = [];
	for (const p of palabras) {
		let desde = 0;
		for (;;) {
			const i = base.indexOf(p, desde);
			if (i < 0) break;
			marcas.push([i, i + p.length]);
			desde = i + p.length;
		}
	}
	if (marcas.length === 0) return <>{texto}</>;
	marcas.sort((a, b) => a[0] - b[0]);
	const partes: React.ReactNode[] = [];
	let pos = 0;
	marcas.forEach(([a, b], k) => {
		if (a < pos) return;
		if (a > pos) partes.push(texto.slice(pos, a));
		partes.push(<mark key={k}>{texto.slice(a, b)}</mark>);
		pos = b;
	});
	if (pos < texto.length) partes.push(texto.slice(pos));
	return <>{partes}</>;
};

const Bloques = ({ bloques, palabras }: { bloques: Bloque[]; palabras: string[] }) => (
	<>
		{bloques.map((b, i) => {
			if (b.tipo === 'p') {
				return (
					<p key={i}>
						<Resaltado texto={b.texto ?? ''} palabras={palabras} />
					</p>
				);
			}
			const Lista = b.tipo === 'ol' ? 'ol' : 'ul';
			return (
				<Lista key={i}>
					{(b.items ?? []).map((it, j) => (
						<li key={j}>
							<Resaltado texto={it.texto} palabras={palabras} />
							{it.sub && it.sub.length > 0 && (
								<ul className="sub">
									{it.sub.map((s, k) => (
										<li key={k}>
											<Resaltado texto={s} palabras={palabras} />
										</li>
									))}
								</ul>
							)}
						</li>
					))}
				</Lista>
			);
		})}
	</>
);

const UserManualPage = () => {
	const navigate = useNavigate();
	const [busqueda, setBusqueda] = useState('');
	const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
	const [copiado, setCopiado] = useState('');
	const [arriba, setArriba] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const palabras = useMemo(
		() =>
			normalizar(busqueda)
				.split(/\s+/)
				.filter((p) => p.length >= 2),
		[busqueda],
	);
	const buscando = palabras.length > 0;

	/** Lo que coincide con la búsqueda: todas las palabras tienen que estar en la pregunta. */
	const resultado = useMemo(() => {
		if (!buscando) return MANUAL.map((s) => ({ seccion: s, intro: true, preguntas: s.preguntas }));
		const coincide = (texto: string) => {
			const n = normalizar(texto);
			return palabras.every((p) => n.includes(p));
		};
		return MANUAL.map((s) => ({
			seccion: s,
			intro: s.intro.length > 0 && coincide(`${s.titulo} ${textoDeBloques(s.intro)}`),
			preguntas: s.preguntas.filter((p) => coincide(`${p.titulo} ${textoDeBloques(p.bloques)}`)),
		})).filter((r) => r.intro || r.preguntas.length > 0);
	}, [buscando, palabras]);

	const cantidad = resultado.reduce((n, r) => n + r.preguntas.length + (buscando && r.intro ? 1 : 0), 0);

	const alternar = (id: string) =>
		setAbiertas((prev) => {
			const n = new Set(prev);
			if (n.has(id)) n.delete(id);
			else n.add(id);
			return n;
		});

	/** Ir a una pregunta o sección: la abre, actualiza el enlace y la lleva a la vista. */
	const irA = (id: string, abrir = true, suave = true) => {
		setBusqueda('');
		if (abrir && !id.startsWith('s')) setAbiertas((prev) => new Set(prev).add(id));
		window.history.replaceState(null, '', `#${id}`);
		setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: suave ? 'smooth' : 'auto', block: 'start' }), 60);
	};

	// Llegar con un enlace directo (cambiapp.net/faq#5-9).
	useEffect(() => {
		const alHash = () => {
			const id = decodeURIComponent(window.location.hash.replace('#', ''));
			// Al llegar por un enlace se salta directo, sin animación.
			if (id) irA(id, true, false);
		};
		alHash();
		const alScroll = () => setArriba(window.scrollY > 600);
		window.addEventListener('scroll', alScroll, { passive: true });
		window.addEventListener('hashchange', alHash);
		return () => {
			window.removeEventListener('scroll', alScroll);
			window.removeEventListener('hashchange', alHash);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const copiarEnlace = async (id: string) => {
		const url = `${window.location.origin}/faq#${id}`;
		try {
			await navigator.clipboard.writeText(url);
			setCopiado(id);
			setTimeout(() => setCopiado(''), 2000);
		} catch {
			window.prompt('Copiá el enlace:', url);
		}
	};

	const PreguntaItem = ({ p }: { p: Pregunta }) => {
		const abierta = buscando || abiertas.has(p.id);
		return (
			<div className={`pregunta${abierta ? ' abierta' : ''}`} id={p.id}>
				<button type="button" className="pregunta-titulo" onClick={() => alternar(p.id)} aria-expanded={abierta}>
					<span className="numero">{p.numero}</span>
					<span className="texto">
						<Resaltado texto={p.titulo} palabras={palabras} />
					</span>
					{!buscando && <ExpandMoreIcon className="flecha" />}
				</button>
				{abierta && (
					<div className="respuesta">
						<Bloques bloques={p.bloques} palabras={palabras} />
						<button type="button" className="copiar" onClick={() => copiarEnlace(p.id)}>
							<LinkIcon fontSize="small" /> {copiado === p.id ? 'Enlace copiado' : 'Copiar enlace a esta pregunta'}
						</button>
					</div>
				)}
			</div>
		);
	};

	const SeccionBloque = ({ s, intro, preguntas }: { s: Seccion; intro: boolean; preguntas: Pregunta[] }) => (
		<section className="seccion" id={s.id}>
			<h2>
				<span className="numero">{s.numero}</span> <Resaltado texto={s.titulo} palabras={palabras} />
			</h2>
			{intro && s.intro.length > 0 && (
				<div className="intro">
					<Bloques bloques={s.intro} palabras={palabras} />
				</div>
			)}
			{preguntas.map((p) => (
				<PreguntaItem key={p.id} p={p} />
			))}
		</section>
	);

	return (
		<div className="manual-page">
			<div className="contenedor">
				<button type="button" className="volver" onClick={() => navigate('/')}>
					{'< Volver'}
				</button>

				<header className="cabecera">
					<img src="/cambiapp_logo.png" alt="CambiApp" className="logo" />
					<h1>Manual de usuario</h1>
					<p className="bajada">Preguntas frecuentes · actualizado el {MANUAL_ACTUALIZADO}</p>
				</header>

				<div className="buscador">
					<SearchIcon className="lupa" />
					<input
						ref={inputRef}
						type="search"
						value={busqueda}
						onChange={(e) => setBusqueda(e.target.value)}
						placeholder="Buscá: comisión, cita, aceptación parcial…"
						aria-label="Buscar en el manual"
					/>
					{busqueda && (
						<button type="button" className="limpiar" onClick={() => { setBusqueda(''); inputRef.current?.focus(); }} aria-label="Borrar la búsqueda">
							<CloseIcon fontSize="small" />
						</button>
					)}
				</div>

				{buscando ? (
					<p className="conteo">
						{cantidad === 0
							? `No encontramos nada con «${busqueda.trim()}». Probá con otra palabra.`
							: `${cantidad} resultado${cantidad === 1 ? '' : 's'} para «${busqueda.trim()}»`}
					</p>
				) : (
					<nav className="indice" aria-label="Índice">
						<h2>Índice</h2>
						{MANUAL.map((s) => (
							<details key={s.id}>
								<summary>
									<span className="numero">{s.numero}</span> {s.titulo}
									{s.preguntas.length > 0 && <span className="cuantas">{s.preguntas.length}</span>}
								</summary>
								<ul>
									{s.preguntas.length === 0 ? (
										<li>
											<a href={`#${s.id}`} onClick={(e) => { e.preventDefault(); irA(s.id); }}>
												Ver la sección
											</a>
										</li>
									) : (
										s.preguntas.map((p) => (
											<li key={p.id}>
												<a href={`#${p.id}`} onClick={(e) => { e.preventDefault(); irA(p.id); }}>
													<span className="numero">{p.numero}</span> {p.titulo}
												</a>
											</li>
										))
									)}
								</ul>
							</details>
						))}
					</nav>
				)}

				<main>
					{resultado.map((r) => (
						<SeccionBloque key={r.seccion.id} s={r.seccion} intro={r.intro} preguntas={r.preguntas} />
					))}
				</main>

				<footer className="pie">
					<a href="/user-manual.pdf" target="_blank" rel="noopener noreferrer">
						<PictureAsPdfIcon fontSize="small" /> Descargar el manual en PDF
					</a>
				</footer>
			</div>

			{arriba && (
				<button type="button" className="subir" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Volver arriba">
					<KeyboardArrowUpIcon />
				</button>
			)}
		</div>
	);
};

export default UserManualPage;
